import type { SolutionDetail } from "@/types/detail";
import { SHARED_COMPARISON } from "@/data/detail.shared";
import {
  Stethoscope,
  UtensilsCrossed,
  UsersRound,
  UserCheck,
  LayoutDashboard,
  Gauge,
  ShieldCheck,
  Zap,
  LineChart,
  Users,
  Clock,
} from "@/icons";

export const solutionsB: SolutionDetail[] = [
  {
    slug: "hospital-management",
    title: "Hospital Management",
    domain: "Healthcare",
    tagline: "Coordinate patients, staff and records in one secure system.",
    icon: Stethoscope,
    accent: "#0891B2",
    image: "/images/solutions/hospital_management.webp",
    overview:
      "A hospital management system connects appointments, patient records, billing and staff scheduling — reducing waiting times, protecting sensitive data and letting clinicians focus on care.",
    whoShouldUse: [
      "Hospitals and multi-specialty clinics.",
      "Healthcare groups managing several facilities.",
      "Practices needing secure, compliant records.",
    ],
    challenges: [
      "Paper records that are slow and easy to lose.",
      "Long patient waiting times and scheduling clashes.",
      "Billing and insurance handled manually.",
      "Strict compliance and data-privacy requirements.",
    ],
    modules: [
      { title: "Patient records (EMR)", description: "Secure, complete histories available instantly to care teams." },
      { title: "Appointments", description: "Smart scheduling reduces waiting times and no-shows." },
      { title: "Billing & insurance", description: "Automated invoicing and claims reduce errors and delays." },
      { title: "Staff & rota", description: "Balanced schedules keep departments properly staffed." },
      { title: "Pharmacy & inventory", description: "Track medicines and supplies to avoid shortages." },
      { title: "Reports & compliance", description: "Audit-ready records and operational insight." },
    ],
    benefits: [
      { title: "Shorter waits", description: "Smart scheduling improves patient flow.", icon: Clock },
      { title: "Safer data", description: "Compliant, encrypted records with strict access.", icon: ShieldCheck },
      { title: "Faster billing", description: "Automated invoicing and claims speed cash flow.", icon: LineChart },
      { title: "Better care", description: "Clinicians spend less time on admin, more on patients.", icon: Users },
    ],
    workflow: [
      { label: "Register", description: "Patients are registered with secure, unified records." },
      { label: "Schedule", description: "Appointments are booked around staff and resource availability." },
      { label: "Treat", description: "Clinicians access history and update records at the point of care." },
      { label: "Bill", description: "Charges and insurance claims are generated automatically." },
      { label: "Follow up", description: "Reminders and reports support ongoing care and compliance." },
    ],
    integrations: ["Lab systems", "Pharmacy", "Insurance", "Payment", "SMS/email", "Biometric"],
    typicalUsers: ["Reception", "Doctors & nurses", "Billing teams", "Administrators"],
    comparison: SHARED_COMPARISON,
    faqs: [
      { question: "Is patient data kept secure and compliant?", answer: "Yes — encryption, role-based access and audit trails are built in, with compliance tailored to your region." },
      { question: "Can it manage multiple facilities?", answer: "Yes, healthcare groups can run several sites with central oversight." },
      { question: "Does it handle billing and insurance?", answer: "Yes, invoicing and claims are automated to reduce errors and delays." },
      { question: "Can it integrate with lab or pharmacy systems?", answer: "Yes, it connects to labs, pharmacy and other clinical systems." },
      { question: "Will it reduce waiting times?", answer: "Smart scheduling and streamlined flow help cut waits significantly." },
      { question: "Can staff access it on the move?", answer: "Yes, it's responsive and secure across devices." },
    ],
    relatedServices: ["web-applications", "erp-development", "ai-integration"],
    relatedIndustries: ["healthcare", "fintech", "saas"],
    caseConcept: {
      title: "Lumen Care — hospital platform",
      category: "Hospital Management",
      summary: "A concept platform unifying records, scheduling and billing for a multi-specialty clinic, reducing waits and speeding claims.",
      metrics: [{ label: "Wait time", value: "-30%" }, { label: "Claim speed", value: "+45%" }, { label: "No-shows", value: "-25%" }],
      tech: ["React", "FastAPI", "PostgreSQL"],
      accent: "#0891B2",
    },
  },
  {
    slug: "restaurant-management",
    title: "Restaurant Management",
    domain: "Hospitality",
    tagline: "From orders to kitchen to insights — run service smoothly.",
    icon: UtensilsCrossed,
    accent: "#F59E0B",
    image: "/images/solutions/restaurant_management.webp",
    overview:
      "A restaurant system connects POS, kitchen, inventory and reservations so service runs smoothly, waste drops and owners see exactly what's working across every location.",
    whoShouldUse: [
      "Restaurants, cafés and multi-outlet chains.",
      "Cloud kitchens managing multiple channels.",
      "Owners wanting real-time visibility across sites.",
    ],
    challenges: [
      "Orders lost or delayed between front and kitchen.",
      "Food waste from poor stock control.",
      "No clear view of sales across outlets.",
      "Reservations and tables managed on paper.",
    ],
    modules: [
      { title: "POS & orders", description: "Fast ordering that flows straight to the kitchen." },
      { title: "Kitchen display", description: "Clear tickets reduce mistakes and speed up service." },
      { title: "Menu & recipes", description: "Manage items, pricing and ingredient usage centrally." },
      { title: "Inventory", description: "Ingredient-level stock control cuts waste and cost." },
      { title: "Reservations & tables", description: "Smooth bookings and table turnover." },
      { title: "Sales analytics", description: "See best-sellers and margins across every outlet." },
    ],
    benefits: [
      { title: "Faster service", description: "Orders reach the kitchen instantly and accurately.", icon: Zap },
      { title: "Less waste", description: "Ingredient-level tracking cuts spoilage and cost.", icon: Gauge },
      { title: "Higher margins", description: "Know which items and outlets actually make money.", icon: LineChart },
      { title: "Central control", description: "Manage every location from one dashboard.", icon: ShieldCheck },
    ],
    workflow: [
      { label: "Order", description: "Staff or customers place orders that flow to the kitchen instantly." },
      { label: "Prepare", description: "The kitchen display sequences tickets to keep service smooth." },
      { label: "Serve", description: "Tables and delivery channels are tracked to completion." },
      { label: "Restock", description: "Ingredient usage updates stock and flags reorders." },
      { label: "Analyse", description: "Sales and margin reports guide menu and staffing decisions." },
    ],
    integrations: ["Payment", "Delivery platforms", "Accounting", "Loyalty", "Printers", "Reservations"],
    typicalUsers: ["Waiters", "Kitchen staff", "Managers", "Owners"],
    comparison: SHARED_COMPARISON,
    faqs: [
      { question: "Can it manage multiple outlets?", answer: "Yes, chains and cloud kitchens run centrally with per-site views." },
      { question: "Does it integrate with delivery platforms?", answer: "Yes, orders from delivery channels flow into one system." },
      { question: "Can it control ingredient stock?", answer: "Yes, ingredient-level tracking reduces waste and flags reorders." },
      { question: "Does it handle reservations?", answer: "Yes, bookings and table turnover are managed in the platform." },
      { question: "Will it work offline during service?", answer: "Core POS features can keep working through brief connectivity drops." },
      { question: "Can owners see performance remotely?", answer: "Yes, real-time dashboards are available from anywhere." },
    ],
    relatedServices: ["web-applications", "mobile-app-development", "workflow-automation"],
    relatedIndustries: ["ecommerce", "logistics", "saas"],
    caseConcept: {
      title: "Halcyon Table — restaurant suite",
      category: "Restaurant Management",
      summary: "A concept suite linking POS, kitchen and inventory across outlets, speeding service and cutting food waste.",
      metrics: [{ label: "Service time", value: "-20%" }, { label: "Food waste", value: "-30%" }, { label: "Outlets", value: "6" }],
      tech: ["React", "FastAPI", "PostgreSQL"],
      accent: "#F59E0B",
    },
  },
  {
    slug: "hrms",
    title: "HRMS",
    domain: "People",
    tagline: "People, payroll and performance in one place your team enjoys using.",
    icon: UsersRound,
    accent: "#7C3AED",
    image: "/images/solutions/hrms_solution.webp",
    overview:
      "A human resource management system handles the full employee lifecycle — hiring, onboarding, leave, payroll and reviews — cutting HR admin and giving employees a self-service experience.",
    whoShouldUse: [
      "Growing companies outgrowing HR spreadsheets.",
      "HR teams buried in manual processes.",
      "Businesses wanting self-service for employees.",
    ],
    challenges: [
      "Leave, attendance and payroll tracked by hand.",
      "Onboarding that's slow and inconsistent.",
      "No clear record of performance and reviews.",
      "Employees chasing HR for basic requests.",
    ],
    modules: [
      { title: "Employee records", description: "A single, secure home for all people data." },
      { title: "Leave & attendance", description: "Self-service requests with automatic approvals." },
      { title: "Payroll", description: "Accurate, on-time pay with less manual effort." },
      { title: "Onboarding", description: "Consistent, welcoming starts for every new hire." },
      { title: "Performance", description: "Structured reviews and goals that actually happen." },
      { title: "Self-service portal", description: "Employees handle requests without emailing HR." },
    ],
    benefits: [
      { title: "Less HR admin", description: "Automation frees HR for higher-value work.", icon: Clock },
      { title: "Accurate payroll", description: "Fewer errors and always on time.", icon: ShieldCheck },
      { title: "Happier employees", description: "Self-service removes friction and delays.", icon: Users },
      { title: "Better retention", description: "Structured growth and reviews keep people engaged.", icon: LineChart },
    ],
    workflow: [
      { label: "Hire", description: "Candidates move through a structured hiring pipeline." },
      { label: "Onboard", description: "New starters complete a consistent, guided onboarding." },
      { label: "Manage", description: "Leave, attendance and requests are handled via self-service." },
      { label: "Pay", description: "Payroll runs accurately using live attendance and policy." },
      { label: "Grow", description: "Reviews and goals support development and retention." },
    ],
    integrations: ["Payroll providers", "Accounting", "Slack/email", "SSO", "Biometric", "Job boards"],
    typicalUsers: ["HR teams", "Managers", "Employees", "Finance"],
    comparison: SHARED_COMPARISON,
    faqs: [
      { question: "Does it handle payroll?", answer: "Yes, payroll runs from live attendance and policy, or integrates with your payroll provider." },
      { question: "Is there employee self-service?", answer: "Yes, employees manage leave, details and requests without emailing HR." },
      { question: "Can it manage onboarding?", answer: "Yes, with consistent, guided onboarding for every new hire." },
      { question: "Does it support performance reviews?", answer: "Yes, structured reviews and goals are built in." },
      { question: "Is employee data secure?", answer: "Yes, with role-based access and strong data protection." },
      { question: "Can it scale as we hire?", answer: "Yes, it grows with your headcount and complexity." },
    ],
    relatedServices: ["web-applications", "workflow-automation", "erp-development"],
    relatedIndustries: ["saas", "healthcare", "logistics"],
    caseConcept: {
      title: "Northwind People — HRMS",
      category: "HRMS",
      summary: "A concept HRMS automating leave, payroll and onboarding for a scaling company, cutting HR admin and payroll errors.",
      metrics: [{ label: "HR admin", value: "-55%" }, { label: "Payroll errors", value: "-90%" }, { label: "Onboarding", value: "2x faster" }],
      tech: ["React", "FastAPI", "PostgreSQL"],
      accent: "#7C3AED",
    },
  },
  {
    slug: "customer-portal",
    title: "Customer Self-Service Portal",
    domain: "Customer Experience",
    tagline: "Let customers help themselves — accounts, billing, tickets and docs.",
    icon: LayoutDashboard,
    accent: "#0891B2",
    image: "/images/solutions/customer_portal.webp",
    overview:
      "A self-service portal gives customers a single, secure place to manage their account, view billing, raise tickets and find answers — reducing support load and boosting satisfaction.",
    whoShouldUse: [
      "SaaS and subscription businesses.",
      "Service providers with recurring customers.",
      "Teams overwhelmed by repetitive support requests.",
    ],
    challenges: [
      "Support flooded with routine account questions.",
      "Customers unable to access their own information.",
      "Billing and invoice queries handled manually.",
      "No single place for customers to self-serve.",
    ],
    modules: [
      { title: "Account management", description: "Customers update their own details and preferences." },
      { title: "Billing & invoices", description: "Self-serve access to statements and payments." },
      { title: "Support tickets", description: "Raise and track issues without email back-and-forth." },
      { title: "Knowledge base", description: "Answers to common questions, available 24/7." },
      { title: "Documents", description: "Secure access to contracts, reports and files." },
      { title: "Notifications", description: "Timely updates keep customers informed." },
    ],
    benefits: [
      { title: "Lower support cost", description: "Self-service deflects routine requests.", icon: Gauge },
      { title: "Happier customers", description: "Instant access to what they need, anytime.", icon: Users },
      { title: "Faster payments", description: "Easy self-serve billing improves cash flow.", icon: LineChart },
      { title: "Always available", description: "24/7 answers without waiting on your team.", icon: Clock },
    ],
    workflow: [
      { label: "Sign in", description: "Customers securely access their personalised portal." },
      { label: "Self-serve", description: "They update details, view billing and find answers instantly." },
      { label: "Request", description: "Anything they can't self-serve becomes a tracked ticket." },
      { label: "Resolve", description: "Your team responds in-portal with full context." },
      { label: "Notify", description: "Customers get proactive updates on status and billing." },
    ],
    integrations: ["Payment", "CRM", "Helpdesk", "SSO", "Email/SMS", "Billing"],
    typicalUsers: ["Customers", "Support agents", "Account managers", "Finance"],
    comparison: SHARED_COMPARISON,
    faqs: [
      { question: "Will it reduce our support load?", answer: "Yes — self-service and a knowledge base deflect a large share of routine requests." },
      { question: "Can customers manage billing?", answer: "Yes, they can view invoices, statements and make payments themselves." },
      { question: "Does it integrate with our CRM/helpdesk?", answer: "Yes, it connects to CRM, helpdesk and billing systems." },
      { question: "Is customer data secure?", answer: "Yes, with secure authentication and role-based access." },
      { question: "Can we brand the portal?", answer: "Yes, it's fully branded to feel like part of your product." },
      { question: "Does it support ticketing?", answer: "Yes, customers raise and track tickets with full context." },
    ],
    relatedServices: ["web-applications", "crm-development", "api-development"],
    relatedIndustries: ["saas", "fintech", "ecommerce"],
    caseConcept: {
      title: "Cascade Connect — customer portal",
      category: "Customer Portal",
      summary: "A concept portal giving customers self-serve billing, tickets and docs, cutting support volume and speeding payments.",
      metrics: [{ label: "Tickets", value: "-40%" }, { label: "Payment speed", value: "+30%" }, { label: "CSAT", value: "+18" }],
      tech: ["React", "FastAPI", "PostgreSQL"],
      accent: "#0891B2",
    },
  },
  // {
  //   slug: "visitor-management",
  //   title: "Visitor Management",
  //   domain: "Workplace",
  //   tagline: "Secure, professional check-in that protects people and premises.",
  //   icon: UserCheck,
  //   accent: "#0F766E",
  //   overview:
  //     "A visitor management system replaces the paper logbook with fast, secure digital check-in — pre-registration, badges, host notifications and a clear record of who's on site.",
  //   whoShouldUse: [
  //     "Offices and corporate campuses.",
  //     "Facilities with security or compliance needs.",
  //     "Organisations hosting frequent visitors or contractors.",
  //   ],
  //   challenges: [
  //     "Paper logbooks that are slow and insecure.",
  //     "No real record of who is on site in an emergency.",
  //     "Unprofessional, manual reception experiences.",
  //     "Contractor and compliance checks handled ad hoc.",
  //   ],
  //   modules: [
  //     { title: "Pre-registration", description: "Invite visitors ahead of time for instant arrival." },
  //     { title: "Self check-in", description: "Fast kiosk or QR check-in with a professional feel." },
  //     { title: "Badge printing", description: "Clear identification for everyone on site." },
  //     { title: "Host notifications", description: "Hosts are alerted the moment a guest arrives." },
  //     { title: "Watchlist & compliance", description: "Screening and agreements handled automatically." },
  //     { title: "Evacuation reports", description: "Know exactly who's on site if you need to." },
  //   ],
  //   benefits: [
  //     { title: "Stronger security", description: "Know and control who enters your premises.", icon: ShieldCheck },
  //     { title: "Great first impression", description: "A slick, modern arrival experience.", icon: Users },
  //     { title: "Faster reception", description: "Self check-in removes queues and manual logging.", icon: Zap },
  //     { title: "Compliance ready", description: "Agreements and records handled automatically.", icon: Gauge },
  //   ],
  //   workflow: [
  //     { label: "Invite", description: "Hosts pre-register visitors, who receive arrival details." },
  //     { label: "Arrive", description: "Guests check in via kiosk or QR in seconds." },
  //     { label: "Notify", description: "The host is alerted instantly that their guest has arrived." },
  //     { label: "Badge", description: "A visitor badge is issued and any agreements captured." },
  //     { label: "Depart", description: "Check-out keeps an accurate, real-time on-site record." },
  //   ],
  //   integrations: ["Access control", "Calendar", "Slack/email", "SSO", "Badge printers", "SMS"],
  //   typicalUsers: ["Reception", "Security", "Hosts", "Facilities"],
  //   comparison: SHARED_COMPARISON,
  //   faqs: [
  //     { question: "Can visitors pre-register?", answer: "Yes, hosts can invite guests in advance for instant arrival." },
  //     { question: "Does it print badges?", answer: "Yes, professional visitor badges are printed on check-in." },
  //     { question: "Will hosts be notified automatically?", answer: "Yes, via email, SMS or Slack the moment a guest arrives." },
  //     { question: "Does it help with compliance?", answer: "Yes, agreements, screening and records are captured automatically." },
  //     { question: "Can we see who is on site?", answer: "Yes, with real-time occupancy and evacuation reports." },
  //     { question: "Does it integrate with access control?", answer: "Yes, it can connect to access and security systems." },
  //   ],
  //   relatedServices: ["web-applications", "mobile-app-development", "workflow-automation"],
  //   relatedIndustries: ["fintech", "healthcare", "education"],
  //   caseConcept: {
  //     title: "Vertex Reception — visitor system",
  //     category: "Visitor Management",
  //     summary: "A concept system replacing paper logbooks with QR check-in, host alerts and evacuation reporting across a corporate campus.",
  //     metrics: [{ label: "Check-in", value: "15s" }, { label: "Reception load", value: "-40%" }, { label: "Sites", value: "4" }],
  //     tech: ["React", "FastAPI", "PostgreSQL"],
  //     accent: "#0F766E",
  //   },
  // },
];
